import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import TimesheetDto from '@/models/Timesheets/TimesheetDto';
import TimesheetSearchViewModel from '@/models/Timesheets/TimesheetSearchViewModel';
import TimesheetSearchRequest from '@/models/Timesheets/TimesheetSearchRequest';
import LocationProfileSearchRequest from '@/models/Locations/LocationProfileSearchRequest';
import LocationProfileDto from '@/models/Locations/LocationProfileDto';
import WorkerProfileDto from '@/models/Workers/WorkerProfileDto';
import WorkerProfileSearchRequest from '@/models/Workers/WorkerProfileSearchRequest';
import CompanyProfileDto from '@/models/Companies/CompanyProfileDto';
import CompanyProfileSearchRequest from '@/models/Companies/CompanyProfileSearchRequest';
import TimesheetStatus from '@/models/Timesheets/TimesheetStatus';
import TimesheetViewModel from '@/models/Timesheets/TimesheetViewModel';
import UpdateTimesheetRequest from '@/models/Timesheets/UpdateTimesheetRequest';
import WorkerStatus from '@/models/Workers/WorkerStatus';
import WorkType from '@/models/Shared/WorkType';
import JourneyCalculationType from '@/models/Timesheets/JourneyCalculationType';
import EditTimesheetOptionsResponse from '@/models/Timesheets/EditTimesheetOptionsResponse';
import WorkerMinimumPayRateRequest from '@/models/Workers/WorkerMinimumPayRateRequest';

export interface ITimesheetListState {
  loading: boolean;
  timesheets: TimesheetDto[];
  timesheetSearchViewModel: TimesheetSearchViewModel;
  editTimesheetOptions: EditTimesheetOptionsResponse;
  editTimesheetMinimumPayRate: number;
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'TimesheetList', namespaced: true})
class TimesheetList extends VuexModule implements ITimesheetListState {
  
  public loading = false;
  public timesheets: TimesheetDto[] = [];  
  public timesheetSearchViewModel: TimesheetSearchViewModel = new TimesheetSearchViewModel();
  public editTimesheetOptions: EditTimesheetOptionsResponse = new EditTimesheetOptionsResponse();
  public editTimesheetMinimumPayRate = -1;
  public loadingDialog = false;

  @Action
  async searchTimesheets() {
    const request = new TimesheetSearchRequest();

    if (this.timesheetSearchViewModel.selectedBranch != null) {
      request.branchId = this.timesheetSearchViewModel.selectedBranch.id;
    }

    if (this.timesheetSearchViewModel.selectedDesk != null) {
      request.deskId = this.timesheetSearchViewModel.selectedDesk.id;
    }

    if (this.timesheetSearchViewModel.status != null) {
      request.status = this.timesheetSearchViewModel.status;
    }

    request.startAtFrom = this.timesheetSearchViewModel.startAtFromTo[0];
    request.startAtTo = this.timesheetSearchViewModel.startAtFromTo[1];

    if (this.timesheetSearchViewModel.selectedCompany != null) {
      request.companyId = this.timesheetSearchViewModel.selectedCompany.id;
    }

    if (this.timesheetSearchViewModel.selectedLocation != null) {
      request.locationId = this.timesheetSearchViewModel.selectedLocation.id;
    }

    if (this.timesheetSearchViewModel.selectedWorker != null) {
      request.workerId = this.timesheetSearchViewModel.selectedWorker.id;
    }

    this.setLoading(true);
    await ajax.post(`/api/v1/agency/timesheets/search`, request)
    .then(response => {
      this.context.commit("setTimesheets", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  async getCompaniesForSearchOptions() {
    const request = new CompanyProfileSearchRequest();
    //this.setLoading(true);
    await ajax.post(`/api/v1/agency/companies/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setCompanies", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  async getLocationsForSearchOptions() {
    const request = new LocationProfileSearchRequest();
    //this.setLoading(true);
    await ajax.post(`/api/v1/agency/locations/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setLocations", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  async getWorkersForSearchOptions() {
    const request = new WorkerProfileSearchRequest();
    request.statuses.push(WorkerStatus.Active);
    request.workTypes.push(WorkType.Temp);
    //this.setLoading(true);
    await ajax.post(`/api/v1/agency/workers/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setWorkers", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }


  @Action
  async getEditTimesheetOptions(locationId: string) {
    this.setLoadingDialog(true);
    await ajax.get(`/api/v1/agency/locations/${locationId}/timesheet-options`)
    .then(response => {
      this.context.commit("setEditTimesheetOptions", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getEditTimesheetMinimumPayRate(request: WorkerMinimumPayRateRequest) {
    this.setLoadingDialog(true);
    this.context.commit("setEditTimesheetMinimumPayRate", -1);
    await ajax.get(`/api/v1/agency/workers/${request.workerId}/temp/minimum-pay-rate?date=${request.date}`)
    .then(response => {
      this.context.commit("setEditTimesheetMinimumPayRate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }
 
  @Action
  async editTimesheet(timesheetVM: TimesheetViewModel){
    const request = new UpdateTimesheetRequest();
    request.updatedAt = timesheetVM.updatedAt;
    request.startAt = new Date(`${timesheetVM.startAtDate}T${timesheetVM.startAtTime}`)
    const endAt = new Date(`${timesheetVM.startAtDate}T${timesheetVM.endAtTime}`)
    if (request.startAt > endAt){
      endAt.setDate(endAt.getDate() +1)
    }
    request.endAt = endAt
    request.break = timesheetVM.break;
    request.isPaidBreak = timesheetVM.isPaidBreak;
    request.chargeRate = timesheetVM.chargeRate;
    request.payRate = timesheetVM.payRate;
    request.isJourneyTrackingEnabled = timesheetVM.isJourneyTrackingEnabled;
    request.isSplitPayAndCharge = timesheetVM.isSplitPayAndCharge;
    if (request.isJourneyTrackingEnabled) {
      request.journeyType = timesheetVM.journeyType;
      request.journeyCalculationTypeForPay = timesheetVM.journeyCalculationTypeForPay;
      if (request.journeyCalculationTypeForPay == JourneyCalculationType.Manual) {
        request.travelTimeOneWayForPay = timesheetVM.travelTimeOneWayManualForPay;
        request.distanceOneWayForPay = timesheetVM.distanceOneWayManualForPay;
      } else {
        request.journeyTemplateForPayId = timesheetVM.journeyTemplateForPayId;
      }

      request.journeyCalculationTypeForCharge = timesheetVM.journeyCalculationTypeForCharge;
      if (request.journeyCalculationTypeForCharge == JourneyCalculationType.Manual) {
        request.travelTimeOneWayForCharge = timesheetVM.travelTimeOneWayManualForCharge;
        request.distanceOneWayForCharge = timesheetVM.distanceOneWayManualForCharge;
      } else {
        request.journeyTemplateForChargeId = timesheetVM.journeyTemplateForChargeId;
      }

      request.isDriverExpensePayable = timesheetVM.isDriverExpensePayable;
      request.isDriverExpenseChargeable = timesheetVM.isDriverExpenseChargeable;
      request.isTravelTimePayable = timesheetVM.isTravelTimePayable;
      request.isTravelTimeChargeable = timesheetVM.isTravelTimeChargeable;
    }
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/timesheets/${timesheetVM.id}`, request)
    .then(response => {
      this.context.commit("setTimesheetFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async approveTimesheet(timesheetId: string){
        this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/timesheets/${timesheetId}/approve`)
    .then(response => {
      this.context.commit("setTimesheetFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.loading = false;
    this.timesheets = [];
    this.timesheetSearchViewModel = new TimesheetSearchViewModel();
    this.editTimesheetOptions = new EditTimesheetOptionsResponse();
    this.editTimesheetMinimumPayRate = -1;
    this.loadingDialog = false;
   
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setCompanies(companies: CompanyProfileDto[]) {
      this.timesheetSearchViewModel.companies = companies;
  }

  @Mutation
  private async setLocations(locations: LocationProfileDto[]) {
      this.timesheetSearchViewModel.locations = locations;
  }

  @Mutation
  private async setEditTimesheetOptions(editTimesheetOptions: EditTimesheetOptionsResponse) {
    this.editTimesheetOptions = editTimesheetOptions;
  }

  @Mutation
  private async setEditTimesheetMinimumPayRate(minimumPayRate: number) {
    this.editTimesheetMinimumPayRate = minimumPayRate;
  }

  @Mutation
  private async setTimesheets(timesheets: TimesheetDto[]) {
      this.timesheets = timesheets;
  }

  @Mutation
  private async setTimesheetFromEdit(timesheet: TimesheetDto) {

    const timesheetToUpdateIndex = this.timesheets.findIndex((timesheetToFind: TimesheetDto) => timesheetToFind.id == timesheet.id);
    this.timesheets.splice(timesheetToUpdateIndex, 1, timesheet);    
  }

  @Mutation
  private async setWorkers(workers: WorkerProfileDto[]) {
      this.timesheetSearchViewModel.workers = workers;
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }
}

export const TimesheetListModule = getModule(TimesheetList)
